import PaginatedComponent from '../PaginatedComponent';
import { createCustomSlice } from 'core/utils';
import i18n from 'i18nextInstance';
import moment from 'moment';
import orderBy from 'lodash/orderBy';
import {
    getActionLabel,
    getStatus
} from 'shared/fixtures';
import {
    getAdditionalValues,
    parseAbsenceReportStatus,
    parseGender
} from 'shared/utils';

const initialState = {
    absenceReport: {},
    absenceReportLoading: false,
    absenceReportsDownload: null,
    absenceReportsDownloading: false,
    absenceReportsPage: null,
    absenceReportsPageLoading: false,
    attachStatus: {},
    audit: null,
    auditDownload: [],
    auditDownloading: false,
    busy: false,
    creation: '',
    data: {},
    deleteError: '',
    deleteSuccess: false,
    externalLogin: '',
    importErrors: [],
    importGlobalError: '',
    importInfo: {},
    importLoading: false,
    importSucceded: {},
    importUploading: false,
    list: [],
    loading: false,
    page: null,
    pageDownload: null,
    pageDownloading: false,
    pageLoading: false,
    pageRender: null,
    pin: null,
    positions: [],
    positionsLoading: false,
    positionsSucceded: false,
    recap: {},
    recapLoading: false,
    registrationError: {},
    selected: {},
    single: null,
    singleLoading: false,
    updateSuccess: false
};

const patientSlice = createCustomSlice({
    initialState,
    name: 'patient',
    reducers: {
        setPatientAbsenceReportsPage (state, { payload }) {
            state.absenceReportsPage = new PaginatedComponent({
                ...payload,
                dataParser: parseAbsenceReports,
                loadingAction: patientActions.setPatientAbsenceReportsPageLoading,
                setAction: patientActions.setPatientAbsenceReportsPage
            });
        },
        setPatientAudit (state, { payload }) {
            state.audit = new PaginatedComponent({
                ...payload,
                dataParser: parseAudit,
                loadingAction: patientActions.setPatientLoading,
                setAction: patientActions.setPatientAudit
            });
        },
        setPatientList (state, { payload }) {
            state.list = orderBy(payload, ['last_name', 'first_name'], ['asc', 'asc']);
        },
        setPatientPage (state, { payload }) {
            state.page = new PaginatedComponent({
                ...payload,
                dataParser: parsePatients,
                loadingAction: patientActions.setPatientPageLoading,
                setAction: patientActions.setPatientPage
            });
        },
        setPatientRecap (state, { payload }) {
            state.recap = parseRecap(payload);
        }
    },
    saga: [
        'attachPatient',
        'createPatientExternalLogin',
        'createPatient',
        'deletePatient',
        'getAbsenceReport',
        'getAbsenceReportsPage',
        'getAbsenceReportsDownload',
        'getPatientAudit',
        'getPatientAuditDownload',
        'getPatientData',
        'getPatientHistory',
        'getPatientPage',
        'getPatientPageDownload',
        'getPatientPositions',
        'getPatientRecap',
        'getPatientSingle',
        'handleImportPatientsFinished',
        'handleImportPatientsUpdated',
        'importPatients',
        'pinRegistration',
        'sendPatientSocket',
        'updatePatient',
        'updatePatientPositions'
    ]
});

export const parseAbsenceReport = report => {
    const {
        absence_report_reason,
        created_at,
        doctor,
        doctor_from_date,
        doctor_to_date,
        familiar_relation,
        from_date,
        patient: {
            first_name,
            identification,
            last_name
        },
        status,
        to_date
    } = report;
    const { name } = absence_report_reason ?? {};
    return ({
        ...report,
        absence_report_reason: !!name ? name : '',
        date: moment(created_at).format('DD/MM/YYYY HH:mm'),
        doctorName: doctor ? `${ doctor?.last_name }, ${ doctor?.first_name }` : '-',
        parsedFamiliarRelation: familiar_relation ? parseFamiliarRelation(familiar_relation) : '',
        parsedStatus: parseAbsenceReportStatus(status),
        patientDisplay: `${ last_name }, ${ first_name } (${ identification })`,
        processed_range: !!doctor_from_date && !!doctor_to_date ? `${ moment(doctor_from_date).format('DD/MM/YYYY') } - ${ moment(doctor_to_date).format('DD/MM/YYYY') }` : '',
        range: `${ from_date ? moment(from_date).format('DD/MM/YYYY') : '' }${ to_date ? ` - ${ moment(to_date).format('DD/MM/YYYY') }` : '' }`
    });
};

export const parseFamiliarRelation = relation => {
    switch (relation) {
    case ('own'): return i18n.t('Propia');
    case ('parent'): return i18n.t('Padre/Madre');
    case ('son'): return i18n.t('Hijo/a');
    case ('spouse'): return i18n.t('Cónyugue');
    default: return i18n.t('Desconocido');
    }
};

export const parseAbsenceReports = list => list.map(report => parseAbsenceReport(report));

const parseDiagnosisHistory = (history = []) => history.map(item => {
    const { created_at, doctor_full_name, id } = item;
    return ({
        ...item,
        label: `${ moment(created_at).format('DD/MM/YYYY HH:mm') } - ${ doctor_full_name }`,
        value: id
    });
});

export const parsePatients = list => list.map(({
    birth_date,
    created_at,
    gender,
    workplace,
    ...patient
}) => ({
    ...patient,
    birth_date,
    created_at,
    gender,
    parsed_birth_date: birth_date ? moment(birth_date).format('DD/MM/YYYY') : '',
    parsed_created_at: moment(created_at).format('DD/MM/YYYY HH:mm'),
    parsed_gender: parseGender(gender),
    parsed_workplace: workplace?.name ?? ''
}));

const parseRecap = recap => {
    const { call_doctors, call_status, history, type } = recap;
    if (!!call_doctors && call_doctors.length > 0) {
        call_doctors.forEach(doctor => {
            doctor.status = getStatus(type === 'Evolution' ? type : call_status, doctor.status, moment(doctor.call_update));
        });
    }
    return {
        ...recap,
        call_doctors: orderBy(call_doctors, 'call_update', 'desc'),
        history: parseDiagnosisHistory(history)
    };
};

const parseAssignMethod = method => {
    switch (method) {
    case ('turn'): return i18n.t('Turno');
    case ('call'): return i18n.t('Llamada');
    case ('message'): return i18n.t('Mensaje');
    case ('assign_admin'): return i18n.t('Por admin');
    case ('assign_professional'): return i18n.t('Por profesional');
    default: return i18n.t('Desconocido');
    }
};

const parseCreationMethod = method => {
    switch (method) {
    case ('external'): return i18n.t('Externo');
    case ('massive_store'): return i18n.t('Alta masiva');
    case ('register'): return i18n.t('Registro');
    case ('store'): return i18n.t('Usuario');
    case ('store_simple'): return i18n.t('Usuario (simple)');
    case ('turn'): return i18n.t('Turno');
    default: return i18n.t('Desconocido');
    }
};
const parseDetachMethod = additional_values => {
    const {
        doctor_first_name,
        doctor_id,
        doctor_last_name,
        method
    } = additional_values;
    switch (method) {
    case ('admin'): return `
        ${ i18n.t('Origen: Admin - Profesional') }: ${ doctor_last_name }, ${ doctor_first_name } [${ doctor_id }]
    `;
    case ('professional'): return i18n.t('Origen: Profesional');
    default: return '-';
    }
};

const parseLoginMethod = method => {
    switch (method) {
    case ('external'): return i18n.t('Externo');
    case ('regular'): return i18n.t('Tradicional');
    default: return 'Desconocido';
    }
};

const parseModificationMethod = method => {
    switch (method) {
    case ('external'): return i18n.t('Externo');
    case ('massive_store'): return i18n.t('Alta masiva');
    case ('regular'): return i18n.t('Usuario');
    case ('self') : return i18n.t('Propia');
    default: return i18n.t('Desconocido');
    }
};

const parseAdditional = item => {
    const { action, additional_information } = item;
    const additional_values = getAdditionalValues(additional_information);
    switch (action) {
    case ('DoctorDownloadFile'): return additional_values.file;
    case ('DoctorReassignAppointment'): return `
        ${ i18n.t('Turno') }: ${ moment(additional_values.at, 'YYYY-MM-DD HH-mm-ss').format('DD/MM/YYYY HH:mm') }
        (${ additional_values.doctor }) [${ additional_values.id }]
    `;
    case ('PatientAttach'): return `
        ${ i18n.t('Método') }: ${ parseAssignMethod(additional_values.method) }
        - ${ additional_values.doctor_last_name }, ${ additional_values.doctor_first_name } [${ additional_values.doctor_id }]
    `;
    case ('PatientDetach'): return parseDetachMethod(additional_values);
    case ('PatientExternalRecipeSent'): return '';
    case ('PatientCreate'): return `${ i18n.t('Origen') }: ${ parseCreationMethod(additional_values.method) }`;
    case ('PatientDelete'): return `${ i18n.t('Origen') }: ${ parseLoginMethod(additional_values.method) }`;
    case ('PatientDownloadFile'): return additional_values.file;
    case ('PatientLogin'): return `${ i18n.t('Método') }: ${ parseLoginMethod(additional_values.method) }`;
    case ('PatientLogout'): return '';
    case ('PatientModify'): {
        const { patient: { id }, user: { patient_id } } = item;
        return `${ i18n.t('Origen') }: ${ parseModificationMethod(id === patient_id ? 'self' : additional_values.method) }`;
    }
    default: return '';
    }
};

export const parseAudit = list => list.map(item => {
    const {
        action,
        datetime,
        user: {
            id,
            first_name,
            last_name
        } = {}
    } = item;
    return {
        action: getActionLabel(action),
        additional: parseAdditional(item),
        date: moment(datetime).format('DD/MM/YYYY'),
        time: moment(datetime).format('HH:mm'),
        user: `(${ id }) ${ last_name }, ${ first_name }`
    };
});

const parseImportError = error => {
    switch (error) {
    case 'BIRTH_DATE_INVALID_FORMAT': return i18n.t('Fecha de nacimiento inválida');
    case 'COVERAGE_DOES_NOT_EXIST': return i18n.t('Cobertura inexistente');
    case 'DOCTORS_RELATED_REQUIRED': return i18n.t('Es necesario asociar al menos un profesional');
    case 'EMPTY_REQUIRED_VALUES': return i18n.t('Algunos valores requeridos están vacíos');
    case 'PATIENT_ALREADY_EXISTS': return i18n.t('El DNI ya fue utilizado');
    case 'PLAN_DOES_NOT_EXIST': return i18n.t('Plan inexistente');
    case 'SCHEDULE_DOES_NOT_EXIST': return i18n.t('Régimen laboral inexistente');
    case 'SUPERVISOR_DOES_NOT_EXIST': return i18n.t('El supervisor no existe en la plataforma');
    default: return i18n.t('Error desconocido');
    }
};

export const parseImportErrors = list => list.map(({
    message,
    object
}) => ({
    ...object,
    error: parseImportError(message)
}));

export const patientActions = patientSlice.actions;

export default patientSlice.reducer;
